import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { ClientService, httpHeaders } from '../../client.service';
import { HelperService } from '../../helper.service';

import {
  EquipmentInstallationsResponse,
  GatewaysCollectionParams,
  GatewaysCollectionResponse,
  GetCompanyInstallationsCollectionResponse,
  InstallationsCollectionQueryParams,
} from './general-management.model';

@Injectable()
export class GeneralManagementApi {
  constructor(private clientService: ClientService, private helper: HelperService) {}

  /**
   * Returns list of user installations.
   * Depending on role of user calling endpoint, the result will differ:
   *    admin, support international - get all installations
   *    endUser, maintainer, maintainerDeputy, maintainerAdmin, maintainerCEO - get my installations
   *    support local - get all installations whose country is same as caller country
   * @param params
   * @param version
   */
  getInstallationsCollection(
    params: InstallationsCollectionQueryParams = {},
    _version?: string
  ): Observable<EquipmentInstallationsResponse> {
    const endpoint = `iot/v2/equipment/installations`;

    return this.clientService.get(endpoint, this.helper.removeEmptyParams(params), httpHeaders);
  }

  /**
   * Returns list of company installations.
   *    maintainer, maintainerDeputy, maintainerAdmin, maintainerCEO - get installations maintained by users company
   *    admin, support international - get installations maintained by given company
   *    support local - get installations maintained by given company whose country is same as
   * @param companyId
   * @param limit
   * @param cursor
   */
  getCompanyInstallationsCollection(
    companyId: string,
    limit: number,
    cursor?: string
  ): Observable<GetCompanyInstallationsCollectionResponse> {
    let endpoint = `vitoguide/installations/v2/all-gateways?companyNumber=${companyId}&limit=${limit}`;

    if (cursor) {
      endpoint = `${endpoint}&cursor=${cursor}`;
    }

    return this.clientService.get(endpoint, {}, httpHeaders);
  }

  /**
   * Removes installation from system and restores its gateways to factory settings..
   * Depending on role of user calling endpoint, the result will differ:
   *    endUser - remove his own installation
   *    admin, support international - remove any installation
   *    support local read/write - remove installation for the country matching caller country
   * @param installationId
   */
  deleteInstallation(installationId: number): Observable<void> {
    const endpoint = `iot/v2/equipment/installations/${installationId}`;

    return this.clientService.delete(endpoint);
  }

  /**
   * @param installationId
   * @param gatewaySerial
   */
  deleteGateway(installationId: number, gatewaySerial: string): Observable<void> {
    const endpoint = `iot/v2/equipment/installations/${installationId}/gateways/${gatewaySerial}`;

    return this.clientService.delete(endpoint);
  }

  /**
   * Returns list of gateways based on serial substring.
   * @param params
   */
  getGatewaysCollection(params: GatewaysCollectionParams): Observable<GatewaysCollectionResponse> {
    const endpoint = `iot/v2/equipment/gateways`;

    return this.clientService.get(endpoint, this.helper.removeEmptyParams(params), httpHeaders);
  }
}
